import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Stack, Button, TextField, Select, MenuItem, FormControl,
  InputLabel, Switch, FormControlLabel, IconButton, CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Save as SaveIcon, Refresh as RefreshIcon, LocalOffer as CouponIcon,
} from '@mui/icons-material';
import api from '../api/axios';
import { color } from '../theme/tokens';
import {
  PageHeader, DataTable, StatusBadge, ConfirmDialog, FormDialog, Toolbar, useFeedback,
} from '../components/ui';

const fmtINR = (v) => '₹' + Number(v || 0).toLocaleString('en-IN');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const emptyCoupon = () => ({
  code: '', description: '', discount_type: 'percent', discount_value: 0,
  max_discount: '', min_order_value: 0, max_uses: '', valid_until: '', is_active: 1,
});

const CouponDialog = ({ open, onClose, coupon, onSaved }) => {
  const { success, error } = useFeedback();
  const [form, setForm] = useState(emptyCoupon());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const isEdit = !!coupon?.id;

  useEffect(() => {
    if (!open) return;
    setForm(coupon ? {
      code: coupon.code || '',
      description: coupon.description || '',
      discount_type: coupon.discount_type || 'percent',
      discount_value: coupon.discount_value ?? 0,
      max_discount: coupon.max_discount ?? '',
      min_order_value: coupon.min_order_value ?? 0,
      max_uses: coupon.max_uses ?? '',
      valid_until: coupon.valid_until ? new Date(coupon.valid_until).toISOString().slice(0, 16) : '',
      is_active: coupon.is_active ?? 1,
    } : emptyCoupon());
    setErr('');
  }, [open, coupon]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.code || !form.discount_value) { setErr('Code and discount value required'); return; }
    setSaving(true); setErr('');
    try {
      const payload = {
        ...form,
        max_discount: form.max_discount === '' ? null : parseFloat(form.max_discount),
        max_uses: form.max_uses === '' ? null : parseInt(form.max_uses),
        valid_until: form.valid_until || null,
      };
      if (isEdit) {
        await api.put(`/api/v2/coupons/${coupon.id}`, payload);
        success('Coupon updated');
      } else {
        await api.post('/api/v2/coupons', payload);
        success('Coupon created');
      }
      onSaved();
    } catch (e) {
      error(e.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit coupon' : 'New coupon'}
      subtitle={isEdit ? 'Update the coupon rules' : 'Create a discount code for your store'}
      icon={<CouponIcon sx={{ fontSize: 18 }} />}
      maxWidth="md"
      error={err}
      saving={saving}
      actions={
        <>
          <Button onClick={onClose} disabled={saving} variant="outlined" color="inherit">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} variant="contained" startIcon={<SaveIcon />}>
            {saving ? <CircularProgress size={18} color="inherit" /> : (isEdit ? 'Update' : 'Create')}
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
          <TextField
            fullWidth size="small" label="Coupon code" required
            value={form.code} onChange={e => set('code', e.target.value.toUpperCase())}
            inputProps={{ style: { textTransform: 'uppercase' } }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Type</InputLabel>
            <Select value={form.discount_type} label="Type" onChange={e => set('discount_type', e.target.value)}>
              <MenuItem value="percent">Percentage (%)</MenuItem>
              <MenuItem value="fixed">Fixed (₹)</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TextField
          fullWidth size="small" label="Description"
          value={form.description} onChange={e => set('description', e.target.value)}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth size="small" type="number" required
            label={`Discount value ${form.discount_type === 'percent' ? '(%)' : '(₹)'}`}
            value={form.discount_value} onChange={e => set('discount_value', parseFloat(e.target.value) || 0)}
          />
          {form.discount_type === 'percent' && (
            <TextField
              fullWidth size="small" type="number" label="Max discount (₹)"
              value={form.max_discount} onChange={e => set('max_discount', e.target.value)}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth size="small" type="number" label="Min order value (₹)"
            value={form.min_order_value} onChange={e => set('min_order_value', parseFloat(e.target.value) || 0)}
          />
          <TextField
            fullWidth size="small" type="number" label="Max uses"
            value={form.max_uses} onChange={e => set('max_uses', e.target.value)}
          />
        </Box>

        <TextField
          fullWidth size="small" type="datetime-local" label="Valid until"
          value={form.valid_until} onChange={e => set('valid_until', e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <FormControlLabel
          control={<Switch checked={form.is_active === 1} onChange={e => set('is_active', e.target.checked ? 1 : 0)} />}
          label="Active"
        />
      </Stack>
    </FormDialog>
  );
};

const CouponsV2 = () => {
  const { success, error } = useFeedback();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCoupon, setEditCoupon] = useState(null);
  const [confirmCoupon, setConfirmCoupon] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortFilter, setSortFilter] = useState('newest');

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/v2/coupons', {
        params: { search: search || undefined, status: statusFilter, sort: sortFilter },
      });
      setCoupons(res.data.data || []);
    } catch (e) {
      error(e.response?.data?.message || 'Failed to load coupons');
    } finally { setLoading(false); }
  }, [error, search, statusFilter, sortFilter]);

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  // Debounce the search box
  useEffect(() => {
    const t = setTimeout(() => setSearch(inputVal), 400);
    return () => clearTimeout(t);
  }, [inputVal]);

  const handleNew = () => { setEditCoupon(null); setDialogOpen(true); };
  const handleEdit = (c) => { setEditCoupon(c); setDialogOpen(true); };

  const confirmDelete = async () => {
    if (!confirmCoupon) return;
    try {
      setDeleting(true);
      await api.delete(`/api/v2/coupons/${confirmCoupon.id}`);
      await fetchCoupons();
      success('Coupon deleted');
    } catch (e) {
      error(e.response?.data?.message || 'Failed to delete coupon');
    } finally {
      setDeleting(false);
      setConfirmCoupon(null);
    }
  };

  const columns = [
    {
      key: 'code',
      label: 'Code',
      width: 150,
      render: (v) => (
        <StatusBadge
          tone="gold"
          dot={false}
          label={v}
          sx={{ fontFamily: 'monospace', letterSpacing: '0.04em' }}
        />
      ),
    },
    { key: 'description', label: 'Description', sx: { minWidth: 200 } },
    {
      key: 'discount_value',
      label: 'Discount',
      align: 'right',
      render: (v, row) => (
        <Typography component="span" sx={{ fontWeight: 600, color: color.ink[900] }}>
          {row.discount_type === 'percent' ? `${v}%` : fmtINR(v)}
          {row.discount_type === 'percent' && row.max_discount ? ` (max ${fmtINR(row.max_discount)})` : ''}
        </Typography>
      ),
    },
    {
      key: 'min_order_value',
      label: 'Min order',
      align: 'right',
      render: (v) => fmtINR(v),
    },
    {
      key: 'used_count',
      label: 'Uses',
      align: 'center',
      render: (v, row) => `${v || 0}${row.max_uses ? ` / ${row.max_uses}` : ''}`,
    },
    {
      key: 'valid_until',
      label: 'Valid until',
      render: (v) => {
        if (!v) return <Typography component="span" sx={{ color: color.ink[400] }}>No expiry</Typography>;
        const expired = new Date(v) < new Date();
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography component="span" sx={{ fontSize: '0.8125rem', color: expired ? color.ink[500] : color.ink[900] }}>
              {fmtDate(v)}
            </Typography>
            {expired && <StatusBadge tone="danger" label="Expired" dot={false} />}
          </Box>
        );
      },
    },
    {
      key: 'is_active',
      label: 'Active',
      align: 'center',
      render: (v) => (v ? <StatusBadge tone="success" label="Active" /> : <StatusBadge tone="neutral" label="Inactive" />),
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'center',
      width: 120,
      render: (_, row) => (
        <Stack direction="row" spacing={0.5} justifyContent="center">
          <IconButton size="small" onClick={() => handleEdit(row)} aria-label={`Edit ${row.code}`}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small" onClick={() => setConfirmCoupon(row)} aria-label={`Delete ${row.code}`}
            sx={{ color: color.danger.text, '&:hover': { bgcolor: color.danger.bg, color: color.danger.text } }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        eyebrow="Marketing"
        title="Coupons"
        subtitle={`${coupons.length} coupons`}
        actions={
          <>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchCoupons} disabled={loading}>
              Refresh
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
              Add coupon
            </Button>
          </>
        }
      />

      <Box sx={{ mb: 2 }}>
        <Toolbar
          search={inputVal}
          onSearch={setInputVal}
          searchPlaceholder="Search by code or description..."
          onRefresh={fetchCoupons}
          refreshing={loading}
        >
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="coupon-status-label">Status</InputLabel>
            <Select
              labelId="coupon-status-label"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel id="coupon-sort-label">Sort By</InputLabel>
            <Select
              labelId="coupon-sort-label"
              label="Sort By"
              value={sortFilter}
              onChange={(e) => setSortFilter(e.target.value)}
            >
              <MenuItem value="newest">Newest first</MenuItem>
              <MenuItem value="oldest">Oldest first</MenuItem>
              <MenuItem value="code-asc">Code A–Z</MenuItem>
              <MenuItem value="code-desc">Code Z–A</MenuItem>
              <MenuItem value="uses-desc">Most used</MenuItem>
              <MenuItem value="expiry-asc">Expiring soonest</MenuItem>
            </Select>
          </FormControl>
        </Toolbar>
      </Box>

      <DataTable
        columns={columns}
        rows={coupons}
        rowKey="id"
        loading={loading}
        skeletonRows={6}
        minWidth={980}
        emptyTitle={search || statusFilter !== 'all' ? 'No matching coupons' : 'No coupons yet'}
        emptyDescription={search || statusFilter !== 'all'
          ? 'Try a different search term or status filter.'
          : 'Create a coupon to offer discounts to your customers.'}
        emptyActionLabel={search || statusFilter !== 'all' ? undefined : 'Add coupon'}
        onEmptyAction={search || statusFilter !== 'all' ? undefined : handleNew}
      />

      <CouponDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        coupon={editCoupon}
        onSaved={() => { setDialogOpen(false); fetchCoupons(); }}
      />

      <ConfirmDialog
        open={!!confirmCoupon}
        onClose={() => setConfirmCoupon(null)}
        onConfirm={confirmDelete}
        title="Delete coupon"
        description={confirmCoupon ? `Delete coupon "${confirmCoupon.code}"? This action cannot be undone.` : ''}
        confirmLabel="Delete"
        tone="danger"
        loading={deleting}
      />
    </Box>
  );
};

export default CouponsV2;
